'use client';

import type { ProductCrossChannelMatch } from 'src/types/product-rankings/product-rankings-ops';

import { Box, Card, Stack, Tooltip, Typography, IconButton } from '@mui/material';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';

import { RankSparkline } from './market-rankings-charts';
import { ymdToShort, formatPrice } from '../utils/market-rankings-format';
import { Tag, RankBadge, RankChangeBadge } from './market-rankings-badges';
import { MATCH_LABEL, SOURCE_COLOR, SOURCE_LABEL } from '../constants/market-rankings-labels';


/** 스파크라인을 그리려면 순위가 있는 점이 최소 둘은 있어야 한다 */
const MIN_TREND_POINTS = 2;

/**
 * 다른 채널에서 찾은 같은 상품 한 장.
 *
 * ⚠️ **같은 상품이라는 보장이 없다.** 채널마다 상품명 표기가 달라 브랜드+상품명 유사도로
 * 맞춘 것이라, 확신도(`match`)를 배지로 밝히고 툴팁에 유사도 %를 적는다.
 */
export default function CrossChannelCard({ item }: { item: ProductCrossChannelMatch }) {
  const price = formatPrice(item.displayPrice, item.currency);
  const accent = SOURCE_COLOR[item.source] || '#637381';
  const points = item.points || [];
  const hasTrend = points.filter((point) => point.rank != null).length >= MIN_TREND_POINTS;
  const discountRate = item.discountRate ?? 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: RADIUS.md, p: 1.5, borderColor: 'divider' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
        <Typography sx={{ typography: 'uiStrong', flex: 1, minWidth: 0 }} noWrap>
          {item.sourceLabel || SOURCE_LABEL[item.source] || item.source}
        </Typography>
        <Tooltip title={`상품명 유사도 ${Math.round((item.similarity || 0) * 100)}%`}>
          <Box component="span" sx={{ display: 'inline-flex' }}>
            <Tag
              sx={{
                typography: 'uiMeta',
                bgcolor: item.match === 'similar' ? 'warning.lighter' : 'success.lighter',
                color: item.match === 'similar' ? 'warning.dark' : 'success.dark',
              }}
            >
              {MATCH_LABEL[item.match] || '유사 상품'}
            </Tag>
          </Box>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Stack alignItems="center" spacing={0.25} sx={{ width: 34, flexShrink: 0 }}>
          <RankBadge rank={item.rank} size={28} />
          <RankChangeBadge change={item.rankChange} delta={item.rankDelta} />
        </Stack>
        <Stack spacing={0.1} sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ typography: 'uiAction', lineHeight: 1.3 }} noWrap>
            {item.title}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {price ? (
              <Typography sx={{ fontWeight: 600, typography: 'uiMeta' }}>
                {discountRate > 0 ? (
                  <Box component="span" sx={{ color: 'error.main', mr: 0.5 }}>{discountRate}%</Box>
                ) : null}
                {price}
              </Typography>
            ) : (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>가격 미제공</Typography>
            )}
            <Typography variant="caption" sx={{ color: 'text.disabled', typography: 'uiMeta' }}>
              {ymdToShort(item.date)} 기준{item.kind === 'new' ? ' · 신상' : ''}
            </Typography>
          </Stack>
        </Stack>
        {item.link ? (
          <IconButton size="small" href={item.link} target="_blank" rel="noopener">
            <Iconify icon="solar:square-top-down-linear" width={15} />
          </IconButton>
        ) : null}
      </Stack>

      {hasTrend ? (
        <Box sx={{ mt: 0.5, mx: -0.5 }}>
          <RankSparkline points={points} color={accent} />
        </Box>
      ) : null}
    </Card>
  );
}
